import { Stack, Typography } from '@mui/material';
import { useProductsTFunction } from '@retail/products/i18n';
import { MgSupplierItemClimate } from '@retail/products/types';
import { GWPNumberElement } from './GWPNumberElement';
import { EpdContent } from './EpdContent';
import { TotalEmissionsContent } from './TotalEmissionsContent';
import { Paper } from '@shared/components';
import { ProductInformationCard } from '../ProductInformation/ProductInformationCard';

interface Props {
  data?: MgSupplierItemClimate['data'];
}

export const GwpPhases = {
  A1ToA3: 'A1-A3',
  A4: 'A4',
  A5: 'A5',
  B: 'B',
  C: 'C',
  D: 'D',
} as const;

export type GwpPhasesType = (typeof GwpPhases)[keyof typeof GwpPhases];

const useGwpTotalTitle = (data?: MgSupplierItemClimate['data']) => {
  const t = useProductsTFunction();
  const title = t('products.mgItem.gwp.title');
  if (!data) return title;

  const epdGwpA = data.extractedFromEpd?.epdDataGwpA;
  const usesEpdValues =
    epdGwpA?.epdGwpTotalA1A3 !== undefined ||
    epdGwpA?.epdGwpTotalA4 !== undefined ||
    epdGwpA?.epdGwpTotalA5 !== undefined;

  const functionalUnit = data.epdMetaData?.epdFunctionalUnit;
  const unit = usesEpdValues
    ? functionalUnit && t('products.mgItem.gwp.unitPerFunctionalUnit', { unit: functionalUnit })
    : t('products.mgItem.gwp.unitPerKg');

  return unit ? `${title} – ${unit}` : title;
};

export const GWPContent = ({ data }: Props) => {
  const t = useProductsTFunction();
  const title = useGwpTotalTitle(data);

  return (
    <ProductInformationCard title={title}>
      {!data ? (
        <Paper sx={{ padding: 1 }}>{t('products.mgItem.gwp.noGwp')}</Paper>
      ) : (
        <>
          <Stack display="grid" gridTemplateColumns="1fr 1fr 1fr">
            <GWPNumberElement
              title={t('products.mgItem.gwp.A1ToA3')}
              phase={GwpPhases.A1ToA3}
              value={
                data.extractedFromEpd?.epdDataGwpA?.epdGwpTotalA1A3 ??
                data.boverketData?.boverketDataA?.boverketA1A3KG
              }
            />

            <GWPNumberElement
              title={t('products.mgItem.gwp.A4')}
              phase={GwpPhases.A4}
              value={
                data.extractedFromEpd?.epdDataGwpA?.epdGwpTotalA4 ??
                data.boverketData?.boverketDataA?.boverketA4KG
              }
            />

            <GWPNumberElement
              title={t('products.mgItem.gwp.A5')}
              phase={GwpPhases.A5}
              value={
                data.extractedFromEpd?.epdDataGwpA?.epdGwpTotalA5 ??
                data.boverketData?.boverketDataA?.boverketA5KG
              }
            />
          </Stack>

          <Stack display="grid" gridTemplateColumns="1fr 1fr 1fr">
            <GWPNumberElement
              title={t('products.mgItem.gwp.B')}
              phase={GwpPhases.B}
              value={data.extractedFromEpd?.epdDataGwpB?.epdGwpTotalB1B7}
            />

            <GWPNumberElement
              title={t('products.mgItem.gwp.C')}
              phase={GwpPhases.C}
              value={data.extractedFromEpd?.epdDataGwpC?.epdGwpTotalC1C4}
            />

            <GWPNumberElement
              title={t('products.mgItem.gwp.D')}
              phase={GwpPhases.D}
              value={data.extractedFromEpd?.epdDataGwpD?.epdGwpTotalD}
            />
          </Stack>

          <TotalEmissionsContent data={data} />

          <Stack p={1}>
            <Typography variant="h6">
              {t('products.mgItem.gwp.environmentalProductDeclaration')}
            </Typography>
            <EpdContent data={data} />
          </Stack>
        </>
      )}
    </ProductInformationCard>
  );
};
